import { User } from '@/@types/user'
import MoreIcon from '@/assets/more.svg?react'
import DropdownMenu from '@/components/DropdownMenu/DropdownMenu'
import { MenuItemData } from '@/components/DropdownMenu/types'
import { showConfirm } from '@/components/Modal/utils'
import {
	CLEAR_USER_INTERACTIONS_MUTATION_OPTIONS,
	getBlockToggleMutationOptions,
	getUserQueryOptions,
} from '@/utils/fetchers/user'
import queryClient from '@/utils/queryClient'
import { Button, type ButtonProps } from '@mantine/core'
import { useMutation, useSuspenseQuery, UseSuspenseQueryOptions } from '@tanstack/react-query'
import clsx from 'clsx'
import styles from './MoreDropdownButton.module.css'

export interface MoreDropdownButtonProps extends ButtonProps {
	username: string
}

export default function MoreDropdownButton({
	username,
	className,
	...props
}: MoreDropdownButtonProps) {
	const { data: user } = useSuspenseQuery(
		getUserQueryOptions(username) as UseSuspenseQueryOptions<User>
	)
	const { mutateAsync: toggleBlock } = useMutation(getBlockToggleMutationOptions())
	const { mutateAsync: clearUserInteractions, isPending: isClearingUserInteractions } = useMutation(
		CLEAR_USER_INTERACTIONS_MUTATION_OPTIONS
	)

	const menuData: MenuItemData[] = [
		user.blocking
			? { id: 'unblock', title: '解除黑名单' }
			: {
					id: 'block',
					title: '加入黑名单',
					props: {
						className: styles.menuItem,
					},
				},
	]

	return (
		<DropdownMenu
			renderTrigger={(triggerProps) => (
				<Button
					{...(triggerProps as ButtonProps)}
					type="button"
					className={clsx(styles.button, className)}
					size="sm"
					aria-label="更多操作"
					title="更多操作"
					{...props}
				>
					<MoreIcon className={styles.icon} />
				</Button>
			)}
			data={menuData}
			position="bottom-end"
			onSelect={async (id) => {
				const isToBlocking = !user.blocking
				if (isToBlocking) {
					await showConfirm(
						'确认加入黑名单吗？',
						'被拉黑的用户不能关注你，无法访问你的个人主页，无法与你互动；同时，你也不会看到TA的动态以及其他动态下TA的评论，TA发送给你的历史信件也会被删除。拉黑操作后不会产生访问记录。',
						{
							confirmProps: {
								children: '加入黑名单',
							},
							cancelProps: {
								children: '取消',
							},
						}
					)
				}

				await toggleBlock(user.username)

				if (isToBlocking) {
					showConfirm(
						'继续清除他和你的互动吗',
						'已将他加入黑名单。可以继续清除他与你的所有互动，包括评论、夸夸、日记留言和信件等。清除后互动内容不可恢复。',
						{
							confirmProps: {
								children: '确认清除',
							},
							cancelProps: {
								children: '取消',
							},
						}
					)
						.then(() => clearUserInteractions(user.username))
						.then(() =>
							queryClient.invalidateQueries({
								queryKey: getUserQueryOptions(user.username).queryKey,
							})
						)
				}
			}}
		/>
	)
}
